/*
 * Testimonial Carousel — native port of the winning FigPii R_HP_Testimonial
 * variant (experiment 359030 / variation 63931). Reproduces the exact DOM,
 * Swiper 11 config, and read-more behavior of the live variant.
 *
 * Data source (parity with the FigPii variant):
 *   1. window.jdgmCarouselMode  — reuse Judge.me app-block data if present on
 *      the page (keeps card->modal capability).
 *   2. Fallback: fetch https://cdn.judge.me/reviews/reviews_for_carousel
 *      (same endpoint + params the app-block uses).
 *
 * Swiper (11.0.5) + its CSS are vendored in /assets and injected lazily the
 * first time a section scrolls near the viewport.
 *
 * All review data is written via textContent (never innerHTML) so untrusted
 * review copy cannot inject markup. The only innerHTML assignment is a static
 * SVG string literal (the verified-buyer badge).
 */
(function () {
  'use strict';

  var PREFIX = 'mdtc-';
  var S = function (s) { return PREFIX + s; };

  // Minimal DOM builder — text/attrs/children only, no HTML strings for data.
  function h(tag, cls, opts) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    opts = opts || {};
    if (opts.text != null) el.textContent = opts.text;
    if (opts.attrs) Object.keys(opts.attrs).forEach(function (k) { el.setAttribute(k, opts.attrs[k]); });
    if (opts.children) opts.children.forEach(function (c) { if (c) el.appendChild(c); });
    return el;
  }

  function img(src, alt) { return h('img', null, { attrs: { src: src, alt: alt || '' } }); }

  // "2026-07-06T17:52:12.613Z" -> "7/6/2026" (M/D/YYYY, no leading zeros)
  function formatDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d)) return '';
      return (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    } catch (e) { return ''; }
  }

  function starsEl(rating) {
    var n = Math.round(Number(rating) || 0);
    var kids = [];
    for (var i = 1; i <= 5; i++) {
      kids.push(h('span', 'jdgm-star jdgm--' + (i <= n ? 'on' : 'off')));
    }
    // role+label so screen readers announce each card's rating (mirrors the Liquid header stars)
    return h('div', 'jdgm-stars', { attrs: { role: 'img', 'aria-label': n + ' out of 5 stars' }, children: kids });
  }

  function verifiedEl() {
    var icon = h('span', 'jdgm-reviewer-verified-icon');
    // Static SVG string literal only — no dynamic/user content.
    icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2l2.4 1.8 3 .2.9 2.9 2.2 2-1 2.8 1 2.8-2.2 2-.9 2.9-3 .2L12 22l-2.4-1.8-3-.2-.9-2.9-2.2-2 1-2.8-1-2.8 2.2-2 .9-2.9 3-.2L12 2z" fill="currentColor"/><path d="M8.5 12.2l2.3 2.3 4.7-4.7" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return h('div', S('verified'), {
      children: [
        h('div', S('verified-content'), {
          children: [icon, h('span', S('verified-content-text'), { text: 'Verified Customer' })]
        })
      ]
    });
  }

  // Normalize a review (from jdgmCarouselMode OR the API) to one shape.
  function normalize(r) {
    var name = r.reviewer_name || r.name || 'Anonymous';
    return {
      uuid: r.uuid || '',
      // 0 for a missing/unparseable rating, NOT 5 — a review with no rating must
      // not be promoted to a perfect score and sail through the minRating floor.
      rating: Number(r.rating) || 0,
      body: (r.body || '').trim(),
      name: name,
      initial: r.reviewer_initial || name.charAt(0) || 'A',
      date: formatDate(r.created_at),
      verified: !!(r.verified_buyer || r.verified)
    };
  }

  function slideEl(rev, assets, loaded) {
    var initials = h('div', S('intials'), {
      children: [
        h('span', null, { text: rev.initial }),
        h('div', S('initials-image'), { children: [img(assets.userStar)] })
      ]
    });

    var info = h('div', S('info'), {
      children: [
        h('div', S('name'), { text: rev.name }),
        h('div', S('rating-date'), {
          children: [starsEl(rev.rating), h('div', S('date-separator')), h('span', S('date'), { text: rev.date })]
        })
      ]
    });

    var textEl = h('div', S('text'), { text: '"' + rev.body + '"' });

    var contentChildren = [
      h('div', S('quote-icon-bg'), { children: [img(assets.quoteBg)] }),
      h('div', S('user'), { children: [initials, info] }),
      h('div', S('text-container'), { children: [textEl] })
    ];
    if (rev.verified) contentChildren.push(verifiedEl());

    var contentAttrs = (loaded && rev.uuid) ? { 'data-card-uuid': rev.uuid } : null;
    var slideContent = h('div', S('slide-content'), { attrs: contentAttrs, children: contentChildren });

    return h('div', 'swiper-slide', {
      children: [
        // decorative: alt="" and hidden from the accessibility tree
        h('div', S('quote-icon'), { attrs: { 'aria-hidden': 'true' }, children: [img(assets.quote)] }),
        slideContent
      ]
    });
  }

  // Add Read More / Read Less toggles only when the review text actually overflows.
  function wireReadToggle(root) {
    root.querySelectorAll('.' + S('text')).forEach(function (textEl, i) {
      if (textEl.scrollHeight - 1 <= textEl.clientHeight) return; // not clamped -> no toggle

      var more = h('button', S('read-toggle'), { attrs: { type: 'button', id: S('read-more-button-') + i } });
      more.appendChild(h('span', null, { text: 'Read More' }));
      var less = h('button', S('read-toggle'), { attrs: { type: 'button', id: S('read-less-button-') + i } });
      less.appendChild(h('span', null, { text: 'Read Less' }));

      textEl.after(less);
      textEl.after(more);

      more.addEventListener('click', function (e) { e.stopPropagation(); textEl.classList.add(S('expanded')); });
      less.addEventListener('click', function (e) { e.stopPropagation(); textEl.classList.remove(S('expanded')); });
    });
  }

  function loadOnce(tag, attrs) {
    return new Promise(function (resolve, reject) {
      if (document.getElementById(attrs.id)) { resolve(); return; }
      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
      el.onload = function () { resolve(); };
      el.onerror = function () { reject(new Error('failed to load ' + (attrs.src || attrs.href))); };
      document.head.appendChild(el);
    });
  }

  function ensureSwiper(section) {
    loadOnce('link', { id: S('swiper-css'), rel: 'stylesheet', href: section.getAttribute('data-swiper-css') });
    var jsReady = window.Swiper
      ? Promise.resolve()
      : loadOnce('script', { id: S('swiper-js'), src: section.getAttribute('data-swiper-js') });
    return jsReady.then(function () {
      return new Promise(function (resolve) {
        var t0 = Date.now();
        (function poll() {
          if (window.Swiper || Date.now() - t0 > 5000) return resolve(window.Swiper);
          setTimeout(poll, 50);
        })();
      });
    });
  }

  function getReviews(section) {
    // 1. Reuse Judge.me app-block data already on the page (parity path).
    try {
      var mode = window.jdgmCarouselMode;
      if (mode) {
        var k = Object.keys(mode)[0];
        if (k && mode[k] && mode[k].reviews && mode[k].reviews.length) {
          return Promise.resolve({ reviews: mode[k].reviews.slice(), loaded: true });
        }
      }
    } catch (e) { /* fall through to API */ }

    // 2. Fallback: fetch the same endpoint the app-block uses.
    var shop = (window.Shopify && window.Shopify.shop) || section.getAttribute('data-shop');
    var locale = (window.Shopify && window.Shopify.locale) || 'en';
    var url = 'https://cdn.judge.me/reviews/reviews_for_carousel' +
      '?reviews_selection=all&carousel_type=testimonials' +
      '&star_rating=' + encodeURIComponent(section.getAttribute('data-star-rating') || 'all') +
      '&max_reviews=' + encodeURIComponent(section.getAttribute('data-max-reviews') || '20') +
      '&url=' + encodeURIComponent(location.href) +
      '&shop_domain=' + encodeURIComponent(shop) +
      '&platform=shopify&primary_language=' + encodeURIComponent(locale);
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (d) { return { reviews: (d && d.reviews) || [], loaded: false }; });
  }

  function build(section) {
    if (section.dataset.mdtcInit) return;
    section.dataset.mdtcInit = '1';

    var wrapper = section.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    var assets = {
      quote: section.getAttribute('data-asset-quote'),
      quoteBg: section.getAttribute('data-asset-quote-bg'),
      userStar: section.getAttribute('data-asset-user-star')
    };

    // Authoritative rating floor. getReviews() has two sources and only the API
    // fallback honours the Judge.me star_rating param — the jdgmCarouselMode path
    // returns Judge.me's array unfiltered. Filtering here covers both.
    var minRating = parseInt(section.getAttribute('data-min-rating'), 10);
    if (!(minRating >= 1 && minRating <= 5)) minRating = 1; // fail open

    getReviews(section).then(function (res) {
      var reviews = (res.reviews || []).map(normalize).filter(function (rev) {
        return rev.rating >= minRating;
      });
      if (!reviews.length) { section.style.display = 'none'; return; }

      var frag = document.createDocumentFragment();
      reviews.forEach(function (rev) { frag.appendChild(slideEl(rev, assets, res.loaded)); });
      wrapper.textContent = '';
      wrapper.appendChild(frag);

      ensureSwiper(section).then(function (Swiper) {
        if (!Swiper) return;
        new Swiper(section.querySelector('.swiper'), {
          slidesPerView: 1,
          spaceBetween: 20,
          // announce carousel controls as "review" not Swiper's generic "slide"
          // keep in sync with the aria-label on the prev/next <button>s in sections/testimonial-carousel.liquid
          a11y: { prevSlideMessage: 'Previous review', nextSlideMessage: 'Next review' },
          pagination: { el: section.querySelector('.swiper-pagination'), clickable: true },
          navigation: {
            nextEl: section.querySelector('.swiper-button-next'),
            prevEl: section.querySelector('.swiper-button-prev')
          },
          // dragSize 97 matches the scrollbar thumb min-width in the section CSS (mobile).
          scrollbar: { el: section.querySelector('.swiper-scrollbar'), draggable: true, dragSize: 97 },
          on: {
            init: function () { wireReadToggle(section); },
            slideChange: function (sw) {
              section.querySelectorAll('.' + S('text') + '.' + S('expanded')).forEach(function (t) {
                t.classList.remove(S('expanded'));
              });
              if (sw && sw.update) sw.update();
            }
          }
        });
      });
    }).catch(function () { section.style.display = 'none'; });
  }

  function boot() {
    var sections = document.querySelectorAll('.mdtc');
    if (!sections.length) return;
    var io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { obs.unobserve(en.target); build(en.target); }
          });
        }, { rootMargin: '400px 0px' }) // start loading Swiper + reviews 400px before the section enters view
      : null;
    sections.forEach(function (sec) { if (io) io.observe(sec); else build(sec); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
